/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { NavigationEnd, Router } from '@angular/router';
import { LabelsService } from '@services/labels/labels.service';
import { Injectable } from '@angular/core';
import { ModalPrefaceComponent } from '@shared/components/modal-preface/modal-preface.component';
import {
  LoadingController,
  ToastController,
  ModalController,
  AlertController,
  ModalOptions,
  PopoverController
} from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UiService {
  /**
   * configurazione per il toast di errore sulla prossima chiamata che va in errore
   */
  private nextError: { show: boolean; message?: string } = {
    show: true
  };

  /**
   * subject per gestire la valorizzazione del loader id
   */
  private loaderId$: BehaviorSubject<string> = new BehaviorSubject<string>(
    null
  );

  /**
   * numero di elementi che hanno richiesto l'app loader
   */
  private loadingQueue = 0;

  constructor(
    private router: Router,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private modalController: ModalController,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private labelsService: LabelsService
  ) {}

  /**
   * visalizza app loader
   */
  async showAppLoader(message?: string): Promise<void> {
    this.loadingQueue++;

    if (this.loadingQueue === 1) {
      await this.createLoader(message);
    }
  }

  /**
   * nasconde app loader
   */
  async hideAppLoader(force?: boolean): Promise<void> {
    if (force === true) {
      this.loadingQueue = 0;
    } else {
      this.loadingQueue--;
    }

    // se ci sono altri elementi in coda non nascondo il loader
    if (this.loadingQueue > 0) {
      return;
    }

    // è necessario aspettare il subject in quanto l'apertura del loader
    // è un operazione asincrona e nel caso la funzione hideAppLoader venga
    // richiamata immediamente dopo il loader potrebbe non essere ancora visibile
    // e la chiusura sarebbe inefficace
    const id = await this.loaderId$
      .pipe(
        filter(x => !!x),
        first()
      )
      .toPromise();

    this.loadingController.dismiss(null, null, id);
    this.loaderId$.next(null);
  }

  /**
   * visualizza toast di errore
   */
  async showErrorToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'error' });
  }

  /**
   * visualizza toast di successo
   */
  async showSuccessToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'success' });
  }

  /**
   * visualizza toast di warning
   */
  async showWarningToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'warning' });
  }

  /**
   * visualizza toast di info
   */
  async showInfoToast(message: string): Promise<void> {
    await this.showToast(message, { type: 'info' });
  }

  /**
   * mostra toast
   */
  async showToast(
    message: string,
    options?: { type?: 'error' | 'warning' | 'success' | 'info' }
  ): Promise<void> {
    let duration = 2000;

    if (options && (options.type === 'error' || options.type === 'warning')) {
      duration = 4000;
    }

    const toast = await this.toastController.create({
      message: this.labelsService.getLabel(message),
      duration,
      position: 'top',
      color: this.getMessageColor(options && options.type)
    });

    await toast.present();
  }

  /**
   * mostra modale di prefazione
   */
  async showPreface(
    message: string,
    options?: { title?: string; figure?: string; okText?: string }
  ): Promise<void> {
    await this.showModal({
      component: ModalPrefaceComponent,
      componentProps: {
        title: (options && options.title) || null,
        messages: [message],
        figure: (options && options.figure) || null,
        okText: (options && options.okText) || null
      }
    });
  }

  /**
   * apre modale;
   * è possibile specificare il controller adibito ad aprire la modale per
   * garantire che le depency richieste dalla modale siano proveddute
   */
  async showModal<T>(
    options: ModalOptions,
    controller?: ModalController
  ): Promise<T> {
    const modal = await (controller instanceof ModalController
      ? controller
      : this.modalController
    ).create(options);

    await modal.present();

    const { data } = await modal.onWillDismiss<T>();

    return data;
  }

  /**
   * apre modale di alert
   */
  async showAlert(title: string, question: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.labelsService.getLabel(title),
      message: this.labelsService.getLabel(question),
      buttons: [{ role: 'ok', text: this.labelsService.getLabel('btn.ok') }]
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    return role === 'ok';
  }

  /**
   * apre modale di conferma
   */
  async showConfirm(title: string, question: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header: this.labelsService.getLabel(title),
      message: this.labelsService.getLabel(question),
      buttons: [
        {
          role: 'dismiss',
          text: this.labelsService.getLabel('btn.dismiss')
        },
        { role: 'ok', text: this.labelsService.getLabel('btn.confirm') }
      ]
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    return role === 'ok';
  }

  /**
   * chiude la modale che risulta attualmente aperta
   */
  async closeOpenModal(): Promise<void> {
    const modal = await this.modalController.getTop();

    if (modal && modal.id) {
      this.modalController.dismiss(null, null, modal.id);
    }
  }

  /**
   * chiude la modale che risulta attualmente aperta
   */
  async closeOpenPopover(): Promise<void> {
    const popover = await this.popoverController.getTop();

    if (popover && popover.id) {
      this.popoverController.dismiss(null, null, popover.id);
    }
  }

  /**
   * non mostra il messaggio di errore per la prossima chiamata
   */
  hideNextError(): void {
    this.nextError = { show: false };
  }

  /**
   * ripristina la configurazione per la visualizzazione dell'errore per le prossime chiamate
   */
  restoreNextError(): void {
    this.nextError = { show: true };
  }

  /**
   * imposta il messaggio di default per la promissa chiamata
   */
  customizeNextError(message?: string): void {
    this.nextError = { show: true, message: message || undefined };
  }

  /**
   * stabilisce se l'errore è da visualizzare
   */
  isNextErrorToShow(): boolean {
    return this.nextError && this.nextError.show === true;
  }

  /**
   * restituisce il messaggio per l'errore
   */
  getNextErrorMessage(): string {
    return this.nextError && this.nextError.message;
  }

  /**
   * chiude le modali e i pover rimasti eventulamente aperti ad ogni cambio di pagina;
   * assicurarsi di chiamarla una volta sola
   */
  ensureOverlayClosingOnPageChange(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeOpenModal();
        this.closeOpenPopover();
      });
  }

  /**
   * mantiene sincronizzata la variabile css per assicurare il fullscreen;
   * assicurarsi di chiamarla una volta sola
   */
  ensureFullscreen(): void {
    this.updateVhProperty();

    window.addEventListener('resize', () => {
      this.updateVhProperty();
    });
  }

  /**
   * aggiorna la var css vh per calcolare la reale altezza della viewport;
   * questo è un trick per poter definire elementi che siano alti esattamente
   * quanto la finestra ma non risentano della status bar che scompare quando si scrolla
   * (https://css-tricks.com/the-trick-to-viewport-units-on-mobile/)
   */
  updateVhProperty(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  /**
   * crea il loader e aggiorna il subject del loader id
   */
  private async createLoader(message?: string): Promise<void> {
    const loading = await this.loadingController.create({
      cssClass: 'app-loader',
      message: message ? this.labelsService.getLabel(message) : 'Please wait...'
    });

    await loading.present();

    this.loaderId$.next(loading.id);
  }

  /**
   * restituisce il colore configurato per il tipo di messaggio
   */
  private getMessageColor(type: string): string {
    const colorMap = {
      error: 'danger',
      warning: 'warning',
      success: 'success'
    };

    if (!!type && !!colorMap[type]) {
      return colorMap[type];
    } else {
      return undefined;
    }
  }
}
